"use client";

import { ShieldCheck, Scale, FileText, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Standards() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 01.1",
    icon: ShieldCheck,
    title: "Safety",
    italicWord: "First",
    titleSuffix: "",
    description: "We maintain a secure, neutral environment where children feel protected and parents can focus on positive, structured interaction without conflict."
  }, {
    index: "§ 01.2",
    icon: Scale,
    title: "Neutral",
    italicWord: "Guidance",
    titleSuffix: "",
    description: "Our professional monitors provide impartial, trauma-informed support, ensuring all interactions remain child-centered and respectful of court mandates."
  }, {
    index: "§ 01.3",
    icon: FileText,
    title: "Clear",
    italicWord: "Reporting",
    titleSuffix: "",
    description: "We provide objective, detailed observation reports for the court, documenting progress and adherence to parenting time agreements with absolute clarity."
  }];
  return <section data-section-id="2110" id="standardsandresponsibilities" className="relative overflow-hidden bg-[#E7D8C7] py-28 md:py-36" style={{
    color: "#2F3631"
  }}>
      {}
      <div className="absolute inset-0 z-0 opacity-10 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/arizona_warmth.png)] bg-cover bg-fixed bg-center" aria-hidden="true" />

      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block z-10" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #2F3631",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden z-10" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #2F3631",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>

      <div className="relative z-20 mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-8 lg:gap-6 transition-opacity duration-700 ease-out ${headerVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="lg:col-span-8">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              <span style={{
              color: "#5E6A52"
            }}>§ 01</span>
              <span style={{
              color: "#A3B18A"
            }}>—</span>
              <span style={{
              color: "#5E6A52"
            }}>OUR COMMITMENT / STANDARDS</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1" style={{
              backgroundColor: "#A3B18A"
            }} />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#5E6A52"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#5E6A52"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#5E6A52"
              }} />
              </div>
            </div>

            {}
            <h2 className="mt-8" style={{
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "#2F3631"
          }}>
              Our Commitment to Safety and Neutrality
            </h2>
          </div>

          <div className="lg:col-span-8">
            <p className="font-default max-w-2xl" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.55,
            color: "#2F3631"
          }}>
              Tiny to Teen Supervised Parenting & Education Services operates with firm boundaries and compassionate care. We provide the structure families need to navigate court-involved parenting time with confidence and safety.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-opacity ease-out ${cardsVisible ? "opacity-100" : "opacity-0"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform duration-300 ease-out hover:-translate-y-1" style={{
              backgroundColor: "#D9C2A6",
              color: "#2F3631",
              borderRadius: "0.5rem",
              border: "1px solid #A3B18A"
            }}>
                  {}
                  <span aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid #5E6A52",
                borderLeft: "1px solid #5E6A52"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid #5E6A52",
                borderRight: "1px solid #5E6A52"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid #5E6A52",
                borderLeft: "1px solid #5E6A52"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid #5E6A52",
                borderRight: "1px solid #5E6A52"
              }} />

                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                lineHeight: 1
              }}>
                    <span style={{
                  color: "#5E6A52"
                }}>{pillar.index}</span>
                    <span style={{
                  color: "#A3B18A"
                }}>—</span>
                    <span style={{
                  color: "#5E6A52"
                }}>STANDARD</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:rotate-6">
                    <Icon size={32} strokeWidth={1.25} style={{
                  color: "#5E6A52"
                }} />
                  </div>

                  {}
                  <h3 className="mt-7" style={{
                fontFamily: "var(--font-serif)",
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                fontWeight: 600,
                letterSpacing: "-0.012em",
                lineHeight: 1.15,
                color: "#2F3631"
              }}>
                    {pillar.title}{" "}
                    <em style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 400
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  {}
                  <p className="font-default mt-5" style={{
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0.005em",
                lineHeight: 1.65,
                color: "#2F3631"
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1" style={{
                  backgroundColor: "#A3B18A"
                }} />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#5E6A52"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#5E6A52"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#5E6A52"
                  }} />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <button type="button" className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "#5E6A52"
                }}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full" style={{
                      backgroundColor: "#5E6A52"
                    }} />
                      </span>
                      <ArrowRight size={16} strokeWidth={1.25} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </button>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <p className="font-default" style={{
          fontSize: "1.125rem",
          fontWeight: 400,
          lineHeight: 1.6,
          color: "#2F3631",
          opacity: 0.9
        }}>
            Our commitment to professional boundaries ensures that every interaction remains objective and safe. By maintaining a child-centered approach, we provide families with the stability required to navigate complex transitions with dignity and respect.
          </p>
        </div>
      </div>
    </section>;
}